import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { QdsSignInstructions } from "./QdsSignInstructions";
import { QdsFileUpload } from "./QdsFileUpload";
import DigitalSignatureQualifiedGateway from "../../api/gateways/DigitalSignatureQualifiedGateway";
import "./QdsSignModal.style.css";

interface QdsSignModalProps {
  documentId?: number;
  qdsRequestId?: number;
  documentName?: string;
  email?: string;
  token?: string;
  onClose: () => void;
  onConfirm: (file: File) => void;
  onDownload?: () => void | Promise<void>; // Optional custom download handler
  autoDownload?: boolean; // Start document download as soon as the modal opens
}

export const QdsSignModal: React.FC<QdsSignModalProps> = ({
  documentId,
  qdsRequestId,
  documentName,
  email,
  token,
  onClose,
  onConfirm,
  onDownload,
  autoDownload = false,
}) => {
  const { t } = useTranslation();
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const autoDownloadStarted = useRef(false);

  const handleFileSelect = (file: File) => {
    setUploadedFile(file);
  };

  const handleDownload = async () => {
    // If custom download handler is provided, use it
    if (onDownload) {
      await onDownload();
      return;
    }

    // Otherwise, use default download logic for external users
    if (!qdsRequestId || !email || !token) return;

    try {
      const response = await DigitalSignatureQualifiedGateway.getQdsWebSigningDocument(
        qdsRequestId.toString(),
        { email, token }
      );

      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = documentName || "document.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading document:", error);
      alert("Failed to download document");
    }
  };

  useEffect(() => {
    if (autoDownload && !autoDownloadStarted.current) {
      autoDownloadStarted.current = true;
      handleDownload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoDownload]);

  const handleConfirm = () => {
    if (uploadedFile) {
      onConfirm(uploadedFile);
      onClose();
    }
  };

  return (
    <div className="qds-sign-modal-overlay" onClick={onClose}>
      <div className="qds-sign-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="qds-sign-modal-header">
          <h2>{t("WEB_QDS_SIGNATURE.SIGNING_MODAL_TITLE")} {documentId ? `[${documentId}]` : ""}</h2>
          <button className="qds-sign-modal-close" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="qds-sign-modal-separator"></div>

        {/* Body - Two Columns */}
        <div className="qds-sign-modal-body">
          <div className="qds-sign-modal-left">
            <QdsSignInstructions 
              qdsRequestId={qdsRequestId}
              email={email}
              token={token}
              documentName={documentName}
              onDownload={handleDownload}
            />
          </div>
          <div className="qds-sign-modal-right">
            <QdsFileUpload onFileSelect={handleFileSelect} uploadedFile={uploadedFile} />
          </div>
        </div>

        {/* Footer */}
        <div className="qds-sign-modal-footer">
          <button
            className="qds-sign-modal-confirm"
            onClick={handleConfirm}
            disabled={!uploadedFile}
          >
            {t("WEB_QDS_SIGNATURE.CONFIRM_BUTTON")}
          </button>
        </div>
      </div>
    </div>
  );
};
